import { useMemo, useState } from 'react'
import { api, query } from '../api/client'
import { useQuery } from '../api/hooks'
import type {
  CashLocation,
  CashLocationType,
  Currency,
  Party,
  PostableAccount,
} from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import { ErrorBanner, Field, Loading, Modal, Money, Panel, Tag, isoDate } from '../components/ui'

/**
 * Banks and safes — the physical places money sits.
 *
 * Nothing can be entered in the books until at least one of these exists: the seeded chart creates
 * the GL accounts, but a cash location is what turns one of them into a register with a running
 * balance. Each GL account backs exactly one location; sharing one between two safes would make
 * both running balances meaningless.
 */
export function CashLocationsPage() {
  const { can } = useAuth()
  const [creating, setCreating] = useState(false)

  const locations = useQuery<CashLocation[]>('/cash-books/locations')
  const accounts = useQuery<PostableAccount[]>('/accounts/postable')

  const accountsById = useMemo(
    () => new Map((accounts.data ?? []).map((a) => [a.id, a])),
    [accounts.data],
  )

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="spacer" />
        {can(P.accounts.create) && (
          <button className="primary" onClick={() => setCreating(true)}>
            + New bank or safe
          </button>
        )}
      </div>

      <ErrorBanner error={locations.error} />
      {locations.loading && <Loading />}

      {locations.data?.length === 0 && (
        <div className="banner warn">
          <div>
            <strong>No banks or safes exist yet, so the books cannot be opened.</strong> The seeded
            chart of accounts already has the GL accounts — <span className="code">1/2/1</span> for
            the main safe, <span className="code">1/1/1/1</span> for a bank current account,{' '}
            <span className="code">1/1/4</span> for a client trust account. Create a location
            against one of them to start entering movements.
          </div>
        </div>
      )}

      {locations.data && locations.data.length > 0 && (
        <Panel flush>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Code</th>
                  <th style={{ width: 80 }}>Type</th>
                  <th>Name</th>
                  <th style={{ width: 180 }}>العربية</th>
                  <th style={{ width: 80 }}>Currency</th>
                  <th style={{ width: 220 }}>GL account</th>
                  <th style={{ width: 120 }}>Holds</th>
                  <th style={{ width: 130 }} className="num">
                    Opening
                  </th>
                </tr>
              </thead>
              <tbody>
                {locations.data.map((location) => {
                  const account = accountsById.get(location.glAccountId)

                  return (
                    <tr key={location.id}>
                      <td className="code">{location.code}</td>
                      <td>
                        <Tag kind={location.type === 'Bank' ? 'info' : 'default'}>{location.type}</Tag>
                      </td>
                      <td className="strong">{location.nameEn}</td>
                      <td dir="rtl" className="soft">
                        {location.nameAr}
                      </td>
                      <td>{location.currency}</td>
                      <td>
                        {account ? (
                          <>
                            <span className="code">{account.code}</span>{' '}
                            <span className="soft">{account.nameEn}</span>
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        {/* This flag decides whether the balance counts towards the client funds
                            control check. Getting it wrong makes that report silently wrong. */}
                        {location.isClientTrust ? (
                          <Tag kind="warn">client money</Tag>
                        ) : (
                          <span className="muted">office money</span>
                        )}
                      </td>
                      <td className="num">
                        <Money value={location.openingBalance} blankZero />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {locations.data && locations.data.length > 0 && (
        <div className="muted" style={{ fontSize: 12 }}>
          A location marked <strong>client money</strong> has its balance compared against custody
          and settlements held on the client funds control report.
        </div>
      )}

      {creating && (
        <NewCashLocation
          existing={locations.data ?? []}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            locations.reload()
          }}
        />
      )}
    </>
  )
}

function NewCashLocation({
  existing,
  onClose,
  onSaved,
}: {
  existing: CashLocation[]
  onClose: () => void
  onSaved: () => void
}) {
  const accounts = useQuery<PostableAccount[]>('/accounts/postable')
  const currencies = useQuery<Currency[]>('/currencies')
  const employees = useQuery<Party[]>(`/parties${query({ type: 'Employee' })}`)

  const [form, setForm] = useState({
    type: 'Safe' as CashLocationType,
    code: '',
    nameEn: '',
    nameAr: '',
    currency: 'EGP',
    glAccountId: '',
    custodianPartyId: '',
    isClientTrust: false,
    bankName: '',
    accountNumber: '',
    iban: '',
    openingBalance: '',
    openingBalanceDate: isoDate(new Date()),
  })

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((previous) => ({ ...previous, [key]: value }))
  }

  // Only cash-natured leaves can back a location, and each may back only one — the database has a
  // unique index, so offering a taken account would just produce a failed save.
  const taken = useMemo(() => new Set(existing.map((l) => l.glAccountId)), [existing])

  const candidates = useMemo(
    () =>
      (accounts.data ?? []).filter(
        (account) =>
          !taken.has(account.id) &&
          (account.group === 'Bank' || account.group === 'Safe' || account.group === 'ClientTrustBank'),
      ),
    [accounts.data, taken],
  )

  const chosen = candidates.find((a) => a.id === form.glAccountId)

  /**
   * A currency-restricted account imposes its own currency — a USD safe cannot hold EGP, and the
   * posting engine rejects the attempt. Follow the account rather than let the two disagree.
   */
  const currency = chosen?.currencyRestriction ?? form.currency

  async function save() {
    setBusy(true)
    setError(undefined)

    try {
      await api('/cash-locations', {
        method: 'POST',
        body: {
          type: form.type,
          code: form.code.trim(),
          nameEn: form.nameEn.trim(),
          nameAr: form.nameAr.trim(),
          currency,
          glAccountId: form.glAccountId,
          custodianPartyId: form.custodianPartyId || null,
          isClientTrust: form.isClientTrust,
          bankName: form.bankName.trim() || null,
          accountNumber: form.accountNumber.trim() || null,
          iban: form.iban.trim() || null,
          openingBalance: Number(form.openingBalance) || 0,
          openingBalanceDate: form.openingBalanceDate || null,
        },
      })

      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  const canSave = form.code.trim() !== '' && form.nameEn.trim() !== '' && form.glAccountId !== ''

  return (
    <Modal
      title="New bank or safe"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !canSave} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Create'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      {candidates.length === 0 && (
        <div className="banner warn">
          Every cash and bank account in the chart already backs a location. Create a new leaf under{' '}
          <span className="code">1/1</span> or <span className="code">1/2</span> first.
        </div>
      )}

      <div className="grid cols-2">
        <Field label="Type">
          <select value={form.type} onChange={(e) => set('type', e.target.value as CashLocationType)}>
            <option value="Safe">Safe — physical cash</option>
            <option value="Bank">Bank account</option>
          </select>
        </Field>

        <Field label="Code" hint="Short, and used in exports.">
          <input
            value={form.code}
            onChange={(e) => set('code', e.target.value)}
            placeholder={form.type === 'Bank' ? 'BANK-A-EGP' : 'MAIN-SAFE'}
          />
        </Field>

        <Field label="Name (English)">
          <input
            value={form.nameEn}
            onChange={(e) => set('nameEn', e.target.value)}
            placeholder={form.type === 'Bank' ? 'Bank A — EGP current' : 'Main safe'}
          />
        </Field>

        <Field label="Name (Arabic)">
          <input dir="rtl" value={form.nameAr} onChange={(e) => set('nameAr', e.target.value)} />
        </Field>
      </div>

      <Field
        label="GL account"
        hint="Each account backs exactly one location. Accounts already in use are not listed."
      >
        <select value={form.glAccountId} onChange={(e) => set('glAccountId', e.target.value)}>
          <option value="">Choose an account…</option>
          {candidates.map((account) => (
            <option key={account.id} value={account.id}>
              {account.code} · {account.nameEn}
              {account.currencyRestriction ? ` (${account.currencyRestriction})` : ''}
            </option>
          ))}
        </select>
      </Field>

      <div className="grid cols-2">
        <Field
          label="Currency"
          hint={
            chosen?.currencyRestriction
              ? `Fixed by ${chosen.code}, which is a ${chosen.currencyRestriction} account.`
              : undefined
          }
        >
          <select
            value={currency}
            disabled={!!chosen?.currencyRestriction}
            onChange={(e) => set('currency', e.target.value)}
          >
            {currencies.data?.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {c.nameEn}
              </option>
            ))}
          </select>
        </Field>

        {form.type === 'Safe' && (
          <Field label="Custodian" hint="Ties the physical count to a person.">
            <select
              value={form.custodianPartyId}
              onChange={(e) => set('custodianPartyId', e.target.value)}
            >
              <option value="">—</option>
              {employees.data?.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.nameEn}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>

      {form.type === 'Bank' && (
        <div className="grid cols-3">
          <Field label="Bank name">
            <input value={form.bankName} onChange={(e) => set('bankName', e.target.value)} />
          </Field>

          <Field label="Account number">
            <input value={form.accountNumber} onChange={(e) => set('accountNumber', e.target.value)} />
          </Field>

          <Field label="IBAN">
            <input value={form.iban} onChange={(e) => set('iban', e.target.value)} />
          </Field>
        </div>
      )}

      <div className="grid cols-2">
        <Field
          label="Opening balance"
          hint="What was actually in it on the date below. The running balance starts here."
        >
          <input
            className="num"
            inputMode="decimal"
            value={form.openingBalance}
            onChange={(e) => set('openingBalance', e.target.value)}
          />
        </Field>

        <Field label="Opening balance date">
          <input
            type="date"
            value={form.openingBalanceDate}
            onChange={(e) => set('openingBalanceDate', e.target.value)}
          />
        </Field>
      </div>

      <div
        className={form.isClientTrust ? 'banner warn' : 'banner info'}
        style={{ marginTop: 4, marginBottom: 0 }}
      >
        <div>
          <label style={{ textTransform: 'none', fontWeight: 400, fontSize: 13, marginBottom: 0 }}>
            <input
              type="checkbox"
              style={{ width: 'auto', marginRight: 8 }}
              checked={form.isClientTrust}
              onChange={(e) => set('isClientTrust', e.target.checked)}
            />
            This holds <strong>client money</strong>, not the office&apos;s own
          </label>
          <div style={{ marginTop: 6, fontSize: 12 }}>
            Tick this for a segregated trust account, or for a safe that holds client custody cash.
            Its balance is then compared against custody and settlements held on the client funds
            control report. Leaving it off when it should be on makes that report understate the
            cash held and report a shortfall that does not exist.
          </div>
        </div>
      </div>
    </Modal>
  )
}
